// slices/approvalSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  approvalAPI,
  PendingApproval,
  ApprovalResponse,
  ApprovalHistory
} from 'api/approvalAPI';

// Define proper types
interface TransformedPendingApproval extends PendingApproval {
  departmentName: string;
  requestId: number;
  createdAt: string;
}

// Async thunks
export const fetchPendingApprovals = createAsyncThunk(
  'approvals/fetchPending',
  async (company_id: number, { rejectWithValue }) => {
    try {
      const response = await approvalAPI.getPendingApprovals(company_id);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch pending approvals');
    }
  }
);

export const approveRequest = createAsyncThunk(
  'approvals/approve',
  async (request_id: number, { rejectWithValue }) => {
    try {
      const approvalData = {
        status: 'approved',
        notes: 'Request approved via dashboard',
        rejection_reason: '',
        request_id: request_id
      };
      const response = await approvalAPI.approveRequest(
        request_id,
        approvalData
      );
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to approve request');
    }
  }
);

export const rejectRequest = createAsyncThunk(
  'approvals/reject',
  async (request_id: number, { rejectWithValue }) => {
    try {
      const rejectionData = {
        status: 'rejected',
        notes: 'Request rejected via dashboard',
        rejection_reason: 'Rejected by approver',
        request_id: request_id
      };
      const response = await approvalAPI.rejectRequest(
        request_id,
        rejectionData
      );
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to reject request');
    }
  }
);

export const fetchApprovalHistory = createAsyncThunk(
  'approvals/fetchHistory',
  async (
    {
      company_id,
      skip = 0,
      limit = 100
    }: {
      company_id: number;
      skip?: number;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await approvalAPI.getApprovalHistory(
        company_id,
        skip,
        limit
      );
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch approval history');
    }
  }
);

export const fetchApprovedRequests = createAsyncThunk(
  'approvals/fetchApproved',
  async (
    {
      company_id,
      skip = 0,
      limit = 100
    }: {
      company_id: number;
      skip?: number;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await approvalAPI.getApprovedRequests(
        company_id,
        skip,
        limit
      );
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch approved requests');
    }
  }
);

export const assignAssetToRequest = createAsyncThunk(
  'approvals/assignAsset',
  async (
    {
      request_id,
      asset_id
    }: {
      request_id: number;
      asset_id: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await approvalAPI.assignAssetToRequest(
        request_id,
        asset_id
      );
      return { request_id, asset_id, message: response };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to assign asset to request');
    }
  }
);

interface ApprovalState {
  pending: TransformedPendingApproval[];
  approved: TransformedPendingApproval[];
  history: ApprovalHistory[];
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  success: string | null;
}

const initialState: ApprovalState = {
  pending: [],
  approved: [],
  history: [],
  loading: false,
  actionLoading: false,
  error: null,
  success: null
};

const approvalSlice = createSlice({
  name: 'approvals',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearSuccess: state => {
      state.success = null;
    },
    clearMessages: state => {
      state.error = null;
      state.success = null;
    }
  },
  extraReducers: builder => {
    builder
      // Fetch Pending Approvals
      .addCase(fetchPendingApprovals.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.loading = false;
        // Transform API data to match component structure
        state.pending = action.payload.map(
          (request: PendingApproval): TransformedPendingApproval => ({
            ...request,
            departmentName:
              request.asset_name || request.category_name || 'N/A',
            requestId: request.id,
            createdAt: request.created_at
          })
        );
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Approve Request
      .addCase(approveRequest.pending, state => {
        state.actionLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(approveRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = 'Request approved successfully';
        // Remove the approved request from pending list
        state.pending = state.pending.filter(
          request => request.id !== action.payload.request_id
        );
      })
      .addCase(approveRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // Reject Request
      .addCase(rejectRequest.pending, state => {
        state.actionLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(rejectRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = 'Request rejected successfully';
        // Remove the rejected request from pending list
        state.pending = state.pending.filter(
          request => request.id !== action.payload.request_id
        );
      })
      .addCase(rejectRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Approval History
      .addCase(fetchApprovalHistory.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApprovalHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload;
      })
      .addCase(fetchApprovalHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Approved Requests
      .addCase(fetchApprovedRequests.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApprovedRequests.fulfilled, (state, action) => {
        state.loading = false;
        // Transform API data to match component structure
        state.approved = action.payload.map(
          (request: PendingApproval): TransformedPendingApproval => ({
            ...request,
            departmentName:
              request.asset_name || request.category_name || 'N/A',
            requestId: request.id,
            createdAt: request.created_at
          })
        );
      })
      .addCase(fetchApprovedRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Assign Asset to Request
      .addCase(assignAssetToRequest.pending, state => {
        state.actionLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(assignAssetToRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = 'Asset assigned successfully';
        // Remove the request from approved list after assignment
        state.approved = state.approved.filter(
          request => request.id !== action.payload.request_id
        );
      })
      .addCase(assignAssetToRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });
  }
});

// Selectors
export const selectPendingRequests = (state: { approvals: ApprovalState }) =>
  state.approvals.pending;
export const selectApprovedRequests = (state: { approvals: ApprovalState }) =>
  state.approvals.approved;
export const selectPendingLoading = (state: { approvals: ApprovalState }) =>
  state.approvals.loading;
export const selectPendingError = (state: { approvals: ApprovalState }) =>
  state.approvals.error;
export const selectActionLoading = (state: { approvals: ApprovalState }) =>
  state.approvals.actionLoading;
export const selectSuccessMessage = (state: { approvals: ApprovalState }) =>
  state.approvals.success;
export const selectApprovalHistory = (state: { approvals: ApprovalState }) =>
  state.approvals.history;

export const { clearError, clearSuccess, clearMessages } =
  approvalSlice.actions;
export default approvalSlice.reducer;
